import { CommonModule } from '@angular/common';
import { Component, inject, model, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '@app/core/api.service';
import { OrganizationService } from '@app/core/organization.service';
import { SessionService } from '@app/core/session.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';

interface CreateOrgDialogPayload {
  organization_name: string;
  workspace_name: string;
}

@Component({
  selector: 'app-create-org-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DialogModule, InputTextModule, ButtonModule],
  templateUrl: './create-org-dialog.component.html',
  styleUrl: './create-org-dialog.component.scss',
})
export class CreateOrgDialogComponent {
  private fb = inject(FormBuilder);
  private api = inject(ApiService);
  private organizationService = inject(OrganizationService);
  private sessionService = inject(SessionService);
  private router = inject(Router);

  visible = model.required<boolean>();
  submitting = signal(false);
  errorMessage = signal<string | null>(null);

  

  form = this.fb.group({
    organizationName: ['', [Validators.required, Validators.minLength(2)]],
    workspaceName: ['Main Workspace', [Validators.required, Validators.minLength(2)]],
  });

  closeDialog() {
    if (this.submitting()) return;
    this.visible.set(false);
    this.errorMessage.set(null);
  }

  async onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.submitting()) return;

    const organizationName = this.form.controls.organizationName.value?.trim() ?? '';
    const workspaceName = this.form.controls.workspaceName.value?.trim() ?? '';
    if (!organizationName || !workspaceName) return;

    this.submitting.set(true);
    this.errorMessage.set(null);

    try {
      const createdOrg = await this.api.apost<{ id?: string; name?: string }>(
        'organizations',
        { 
          organization_name: organizationName,
          workspace_name: workspaceName
        }
      );
      const organizationId = createdOrg.id;
      if (!organizationId) {
        throw new Error('Organization creation did not return an id');
      }

      this.sessionService.refreshSession();
      await this.sessionService.ensureSessionDataLoaded();

      this.visible.set(false);
      await this.router.navigate(['/app/organizations', organizationId]);
    } catch (error) {
      console.error('Failed to create organization/workspace', error);
      this.errorMessage.set('Could not create organization and workspace. Please try again.');
    } finally {
      this.submitting.set(false);
    }
  }
}
